import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import type { DistribItem } from '../types'

// Rampas monocromáticas de tonos Tailwind (oscuro → claro): cada barra se tiñe
// según su cantidad (mayor = más oscuro), mismo criterio que VerticalBarChart.
const PALETAS: Record<string, string[]> = {
  // Azul corporativo → blue-800…blue-100
  '#57a6d8': ['#1e40af', '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],
  // Verde corporativo → emerald-800…emerald-100
  '#2fbc80': ['#065f46', '#047857', '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'],
  // Morado corporativo → violet-800…violet-100
  '#8d82bc': ['#5b21b6', '#6d28d9', '#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'],
  // Rosa corporativo → rose-800…rose-100
  '#eb6587': ['#9f1239', '#be123c', '#e11d48', '#f43f5e', '#fb7185', '#fda4af', '#fecdd3', '#ffe4e6'],
}

interface HorizontalBarChartProps {
  data: DistribItem[]
  /** Color hex corporativo; selecciona la rampa monocromática de la gráfica */
  color: string
  /** Etiqueta de la serie (tooltip/leyenda) */
  seriesLabel?: string
  /** Alto visible de la gráfica en px; si las barras exceden este alto, hace scroll interno */
  height?: number
  /** Alto reservado por barra (px). Con muchas categorías aparece scroll vertical */
  slotHeight?: number
  /** Grosor máximo de barra */
  maxBarSize?: number
  /** Formateo del valor mostrado al final de la barra */
  valueFormatter?: (v: number) => string
}

const defaultFormatter = (v: number) => v.toLocaleString('es-EC')
const TRUNCADO = 18 // máx. caracteres del nombre de categoría antes de recortar con «…»

export function HorizontalBarChart({
  data,
  color,
  seriesLabel = 'Total',
  height = 340,
  slotHeight = 30,
  maxBarSize = 22,
  valueFormatter = defaultFormatter,
}: HorizontalBarChartProps) {
  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        Sin datos para mostrar.
      </div>
    )
  }

  // Rampa de la gráfica (fallback: color plano si el hex no está mapeado).
  const paleta = PALETAS[color] ?? [color]

  // Tono por barra en función de la cantidad: el valor más alto usa el tono más
  // oscuro (índice 0) y el más bajo el más claro.
  const valores = data.map((d) => d.valor)
  const min = Math.min(...valores)
  const max = Math.max(...valores)
  const colorPorValor = (valor: number) => {
    if (max === min) return paleta[0]
    const t = (valor - min) / (max - min) // 0 (menor) … 1 (mayor)
    const idx = Math.round((1 - t) * (paleta.length - 1))
    return paleta[idx]
  }

  const config = {
    valor: { label: seriesLabel, color: paleta[0] },
  } satisfies ChartConfig

  // Ancho del eje de categorías según el nombre más largo (recortado a TRUNCADO).
  const maxLabelLen = Math.max(...data.map((d) => d.label.length))
  const anchoEjeY = Math.min(maxLabelLen, TRUNCADO) * 6.3 + 12

  // Alto interno: una franja por categoría. Si supera el alto visible, hace scroll.
  const innerHeight = data.length * slotHeight + 16

  return (
    <div className="w-full overflow-y-auto" style={{ height }}>
      <ChartContainer config={config} className="!aspect-auto w-full" style={{ height: innerHeight }}>
        <BarChart
          accessibilityLayer
          layout="vertical"
          data={data}
          margin={{ top: 4, right: 44, bottom: 4, left: 4 }}
        >
          <XAxis type="number" hide />
          <YAxis
            dataKey="label"
            type="category"
            tickLine={false}
            axisLine={false}
            interval={0}
            width={anchoEjeY}
            tick={{ fontSize: 11 }}
            tickFormatter={(value: string) =>
              value.length > TRUNCADO ? value.slice(0, TRUNCADO) + '…' : value
            }
          />
          <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
          <Bar dataKey="valor" radius={[0, 5, 5, 0]} maxBarSize={maxBarSize}>
            {data.map((item, index) => (
              <Cell key={index} fill={colorPorValor(item.valor)} />
            ))}
            <LabelList
              dataKey="valor"
              position="right"
              offset={6}
              className="fill-foreground"
              fontSize={11}
              formatter={valueFormatter}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  )
}
